import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Filter, Monitor, Search, TrendingUp } from 'lucide-react'
import { useDeviceAssetPage } from '@/hooks/asset'
import { Loading } from '@/components/Loading'
import { InlineError } from '@/components/ErrorDisplay'
import { EmptyData } from '@/components/EmptyState'
import { DEVICE_STATUS_LABELS, type DeviceAssetStatus } from '@/api/types/asset'
import { getApiErrorMessage } from '@/api/client'

type StatusFilter = 'all' | DeviceAssetStatus
type StatusDisplayKey = DeviceAssetStatus | 'UNKNOWN'

export default function DeviceList() {
  const [searchKeyword, setSearchKeyword] = useState('')
  const [appliedKeyword, setAppliedKeyword] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [page, setPage] = useState(0)

  const { data, isLoading, error } = useDeviceAssetPage({
    keyword: appliedKeyword || undefined,
    page,
    size: 20,
  })

  const filteredDevices =
    data?.content.filter((device) =>
      statusFilter === 'all' ? true : device.status === statusFilter
    ) ?? []

  const getDeviceStatus = (status?: DeviceAssetStatus | null): StatusDisplayKey => status ?? 'UNKNOWN'

  const statusConfig: Record<StatusDisplayKey, { label: string; class: string }> = {
    ACTIVE: { label: DEVICE_STATUS_LABELS.ACTIVE, class: 'bg-green-100 text-green-600' },
    MAINTENANCE: { label: DEVICE_STATUS_LABELS.MAINTENANCE, class: 'bg-red-100 text-red-600' },
    RETIRED: { label: DEVICE_STATUS_LABELS.RETIRED, class: 'bg-gray-100 text-gray-600' },
    DISPOSED: { label: DEVICE_STATUS_LABELS.DISPOSED, class: 'bg-gray-200 text-gray-500' },
    UNKNOWN: { label: '未设置', class: 'bg-gray-100 text-gray-500' },
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <Loading text="加载设备数据..." />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <InlineError message={getApiErrorMessage(error)} />
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center gap-4">
        <Link to="/dashboard" className="rounded-lg p-2 transition hover:bg-gray-100">
          <TrendingUp className="h-5 w-5 rotate-180 text-gray-600" />
        </Link>
        <div>
          <h1 className="text-xl font-bold text-gray-900">设备档案管理</h1>
          <p className="mt-1 text-sm text-gray-500">医院设备信息与档案记录</p>
        </div>
      </div>

      <div className="mb-6 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="搜索设备名称、序列号、医院..."
              value={searchKeyword}
              onChange={(event) => setSearchKeyword(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  setAppliedKeyword(searchKeyword.trim())
                  setPage(0)
                }
              }}
              className="w-full rounded-xl border border-gray-200 py-2.5 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
            className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">全部状态</option>
            <option value="ACTIVE">在用</option>
            <option value="MAINTENANCE">维修中</option>
            <option value="RETIRED">已退役</option>
            <option value="DISPOSED">已报废</option>
          </select>
          <button
            onClick={() => {
              setSearchKeyword('')
              setAppliedKeyword('')
              setStatusFilter('all')
              setPage(0)
            }}
            className="flex items-center gap-2 rounded-xl bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-200"
          >
            <Filter className="h-4 w-4" />
            重置
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm">
        {filteredDevices.length ? (
          <>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr className="text-left text-sm text-gray-500">
                  <th className="px-5 py-4 font-medium">设备信息</th>
                  <th className="px-5 py-4 font-medium">所属医院</th>
                  <th className="px-5 py-4 font-medium">安装位置</th>
                  <th className="px-5 py-4 font-medium">品牌型号</th>
                  <th className="px-5 py-4 font-medium">序列号</th>
                  <th className="px-5 py-4 font-medium">建档日期</th>
                  <th className="px-5 py-4 font-medium">状态</th>
                  <th className="px-5 py-4 font-medium">操作</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {filteredDevices.map((device) => {
                  const statusInfo = statusConfig[getDeviceStatus(device.status)]
                  return (
                    <tr key={device.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50">
                            <Monitor className="h-6 w-6 text-blue-600" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{device.deviceName}</div>
                            <div className="text-xs text-gray-400">{device.manufacturer || '-'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-gray-600">{device.hospitalName}</td>
                      <td className="px-5 py-4 text-gray-600">{device.installLocation}</td>
                      <td className="px-5 py-4 text-gray-600">{device.deviceModel}</td>
                      <td className="px-5 py-4 font-mono text-xs text-gray-600">{device.deviceSerialNo}</td>
                      <td className="px-5 py-4 text-gray-600">
                        {new Date(device.createdAt).toLocaleDateString('zh-CN')}
                      </td>
                      <td className="px-5 py-4">
                        <span className={`rounded-full px-2 py-1 text-xs font-medium ${statusInfo.class}`}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <Link
                          to={`/supply-chain/device/${device.id}`}
                          className="text-primary-600 hover:underline"
                        >
                          详情
                        </Link>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            <div className="flex items-center justify-between border-t border-gray-100 px-5 py-4">
              <div className="text-sm text-gray-500">
                显示 {filteredDevices.length} 条，共 {data?.totalElements || 0} 条记录
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((current) => Math.max(0, current - 1))}
                  disabled={page === 0}
                  className="rounded border border-gray-200 px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-40"
                >
                  上一页
                </button>
                <span className="text-sm text-gray-600">
                  第 {page + 1} / {data?.totalPages || 1} 页
                </span>
                <button
                  onClick={() => setPage((current) => Math.min(current + 1, (data?.totalPages || 1) - 1))}
                  disabled={page >= (data?.totalPages || 1) - 1}
                  className="rounded border border-gray-200 px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-40"
                >
                  下一页
                </button>
              </div>
            </div>
          </>
        ) : (
          <EmptyData description={appliedKeyword ? '没有找到符合条件的设备' : '暂无设备数据'} />
        )}
      </div>
    </div>
  )
}
